// dashboard/src/services/creditService.ts
import { supabase } from '../services/supabase';

export interface CreditBalance {
  account_id: string;
  credit_balance: number; // Legacy field
  created_at: string;
  updated_at: string;
  recent_transactions: Transaction[];
  usage_summary: {
    last_30_days_usage: number;
    usage_by_day: {
      date: string;
      total_cost: number;
    }[];
  };
  // New dual credit system fields
  total_available_credits: number;
  plan_credits: {
    total_allocated: number;
    used: number;
    remaining: number;
    current_plan: string;
    plan_period_start: string | null;
    plan_period_end: string | null;
  };
  custom_credits: {
    available: number;
  };
}

export interface SubscriptionPlan {
  id: string;
  name: string;
  price_usd: number;
  monthly_credits: number;
  bonus_percentage: number;
  features: string[];
  is_active: boolean;
}

export interface Transaction {
  id: number;
  amount: number;
  type: string; // 'plan_allocation', 'custom_purchase', 'usage_deduction', 'refund', 'bonus'
  credit_source: string; // 'plan_credits', 'custom_credits', 'legacy'
  description: string;
  related_job_id?: string;
  plan_allocation_id?: string;
  razorpay_payment_id?: string;
  razorpay_order_id?: string;
  razorpay_signature?: string;
  payment_gateway?: string;
  transaction_status?: string;
  timestamp: string;
}

export interface TransactionListResponse {
  transactions: Transaction[];
  job_details: Record<string, {
    job_type: string;
    status: string;
  }>;
  pagination: {
    total_count: number;
    limit: number;
    offset: number;
    has_more: boolean;
  };
}

export interface UsageAnalytics {
  time_series: {
    timestamp: string;
    total_cost: number;
    tool_counts: Record<string, number>;
  }[];
  category_breakdown: {
    category: string;
    cost: number;
    percentage: number;
  }[];
  tool_breakdown: {
    tool: string;
    count: number;
    cost: number;
  }[];
  summary_metrics: {
    total_cost: number;
    most_used_tool: string | null;
    most_expensive_category: string | null;
    average_daily_cost: number;
  };
  date_range: {
    start_date: string;
    end_date: string;
    group_by: string;
  };
}

export const getCreditBalance = async (): Promise<CreditBalance> => {
  const { data: sessionData } = await supabase.auth.getSession();

  if (!sessionData.session) {
    throw new Error('Not authenticated');
  }

  const response = await fetch(
    `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/get-credit-balance`,
    {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${sessionData.session.access_token}`,
      },
    }
  );

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || 'Failed to get credit balance');
  }

  return await response.json();
};

export const addCredits = async (creditAmount: number): Promise<{ message: string; previous_balance: number; new_balance: number; transaction_id: number }> => {
  const { data: sessionData } = await supabase.auth.getSession();

  if (!sessionData.session) {
    throw new Error('Not authenticated');
  }

  const response = await fetch(
    `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/add-credits`,
    {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${sessionData.session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ credit_amount: creditAmount }),
    }
  );

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || 'Failed to add credits');
  }

  return await response.json();
};

// Get available subscription plans
export const getSubscriptionPlans = async (): Promise<SubscriptionPlan[]> => {
  const { data: sessionData } = await supabase.auth.getSession();

  if (!sessionData.session) {
    throw new Error('Not authenticated');
  }

  const response = await fetch(
    `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/get-subscription-plans`,
    {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${sessionData.session.access_token}`,
      },
    }
  );

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || 'Failed to get subscription plans');
  }

  return await response.json();
};

// Add custom credits (non-expiring)
export const addCustomCredits = async (creditAmount: number): Promise<{ message: string; previous_balance: number; new_balance: number; transaction_id: number }> => {
  const { data: sessionData } = await supabase.auth.getSession();

  if (!sessionData.session) {
    throw new Error('Not authenticated');
  }

  const response = await fetch(
    `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/add-custom-credits`,
    {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${sessionData.session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ credit_amount: creditAmount }),
    }
  );

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || 'Failed to add custom credits');
  }

  return await response.json();
};

// Subscribe to a plan
export const subscribeToPlan = async (planId: string, paymentVerified: boolean = false): Promise<{ message: string; allocation_id: string; credits_allocated: number }> => {
  const { data: sessionData } = await supabase.auth.getSession();

  if (!sessionData.session) {
    throw new Error('Not authenticated');
  }

  const response = await fetch(
    `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/subscribe-to-plan`,
    {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${sessionData.session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        plan_id: planId,
        payment_verified: paymentVerified
      }),
    }
  );

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || 'Failed to subscribe to plan');
  }

  return await response.json();
};

export const getCreditTransactions = async (
  limit: number = 20,
  offset: number = 0,
  startDate?: string,
  endDate?: string,
  type?: string
): Promise<TransactionListResponse> => {
  console.log('getCreditTransactions called with params:', { limit, offset, startDate, endDate, type });

  const { data: sessionData } = await supabase.auth.getSession();

  if (!sessionData.session) {
    console.error('Not authenticated');
    throw new Error('Not authenticated');
  }

  // Build query parameters
  const params = new URLSearchParams();
  params.append('limit', limit.toString());
  params.append('offset', offset.toString());
  if (startDate) params.append('start_date', startDate);
  if (endDate) params.append('end_date', endDate);
  if (type) params.append('type', type);

  const url = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/get-credit-transactions?${params.toString()}`;
  console.log('Fetching from URL:', url);

  try {
    const response = await fetch(
      url,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${sessionData.session.access_token}`,
        },
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      console.error('Error response from get-credit-transactions:', errorData);
      throw new Error(errorData.error || 'Failed to get credit transactions');
    }

    const data = await response.json();
    console.log('Credit transactions response:', data);
    return data;
  } catch (error) {
    console.error('Error in getCreditTransactions:', error);
    throw error;
  }
};

export const getUsageAnalytics = async (
  startDate?: string,
  endDate?: string,
  groupBy: 'day' | 'week' | 'month' = 'day'
): Promise<UsageAnalytics> => {
  const { data: sessionData } = await supabase.auth.getSession();

  if (!sessionData.session) {
    throw new Error('Not authenticated');
  }

  // Build query parameters
  const params = new URLSearchParams();
  if (startDate) params.append('start_date', startDate);
  if (endDate) params.append('end_date', endDate);
  params.append('group_by', groupBy);

  const response = await fetch(
    `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/get-usage-analytics?${params.toString()}`,
    {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${sessionData.session.access_token}`,
      },
    }
  );

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || 'Failed to get usage analytics');
  }

  return await response.json();
};

// Razorpay API interfaces
export interface RazorpayOrderRequest {
  amount: number;
  currency: string;
}

export interface RazorpayOrderResponse {
  order_id: string;
  amount: number; // Amount in smallest currency unit for Razorpay
  credits: number; // Number of credits being purchased
  currency: string;
  key_id: string;
}

export interface RazorpayVerifyRequest {
  razorpay_order_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
  credits: number; // Number of credits being purchased
  currency: string;
}

export interface RazorpayVerifyResponse {
  success: boolean;
  message: string;
  new_balance: number;
  credits_added: number;
}

// Create a Razorpay order
export const createRazorpayOrder = async (request: RazorpayOrderRequest): Promise<RazorpayOrderResponse> => {
  const { data: sessionData } = await supabase.auth.getSession();

  if (!sessionData.session) {
    throw new Error('Not authenticated');
  }

  const response = await fetch(
    `${process.env.REACT_APP_CLOUDFLARE_WORKER_URL}/api/payment/create-razorpay-order`,
    {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${sessionData.session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    }
  );

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || 'Failed to create Razorpay order');
  }

  return await response.json();
};

// Verify a Razorpay payment
export const verifyRazorpayPayment = async (request: RazorpayVerifyRequest): Promise<RazorpayVerifyResponse> => {
  const { data: sessionData } = await supabase.auth.getSession();

  if (!sessionData.session) {
    throw new Error('Not authenticated');
  }

  const response = await fetch(
    `${process.env.REACT_APP_CLOUDFLARE_WORKER_URL}/api/payment/verify-razorpay-payment`,
    {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${sessionData.session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    }
  );

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || 'Failed to verify Razorpay payment');
  }

  return await response.json();
};
